import { Link } from "react-router-dom";
import {
  AlertCircle,
  ArrowRight,
  Calendar,
  CloudDownload,
  Mail,
  MessageCircle,
  Plus,
  Send,
  Trophy,
  Users,
} from "lucide-react";
import {
  Card,
  CardBody,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/Card";
import { Button } from "@/components/ui/Button";
import { StatusBadge } from "@/components/StatusBadge";
import { useTournaments } from "@/hooks/useTournaments";
import { useClientsStats } from "@/hooks/useClientsStats";
import { useHealth } from "@/hooks/useHealth";
import { formatItalianDate, formatNumber, daysBetween } from "@/lib/format";

export default function Dashboard() {
  const tournaments = useTournaments();
  const stats = useClientsStats();
  const health = useHealth();

  const activeTournaments =
    tournaments.data?.items.filter(
      (t) =>
        t.status !== "COMPLETED" &&
        t.status !== "FAILED" &&
        new Date(t.date) >= new Date(new Date().setHours(0, 0, 0, 0)),
    ) ?? [];

  return (
    <div className="flex flex-col gap-6">
      {/* Header */}
      <div className="flex items-end justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">Dashboard</h1>
          <p className="text-sm text-slate-500">
            Panoramica operativa dei tornei Moving Padel Monza
          </p>
        </div>
        <Link to="/tournaments/new" className="hidden sm:inline-flex">
          <Button variant="primary">
            <Plus className="h-4 w-4" />
            Nuovo torneo
          </Button>
        </Link>
      </div>

      {/* System status banner (solo se degraded) */}
      {health.data && health.data.status !== "ok" ? (
        <div className="flex items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
          <AlertCircle className="h-4 w-4" />
          Sistema in stato degraded — verifica checks in /api/health
        </div>
      ) : null}

      {/* KPI row */}
      <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
        <KpiCard
          icon={<Trophy className="h-4 w-4" />}
          label="Tornei attivi"
          value={
            tournaments.isLoading ? "—" : formatNumber(activeTournaments.length)
          }
          hint={
            tournaments.data
              ? `${tournaments.data.pagination.total} totali`
              : ""
          }
        />
        <KpiCard
          icon={<Users className="h-4 w-4" />}
          label="Clienti Playtomic"
          value={stats.isLoading ? "—" : formatNumber(stats.data?.total ?? 0)}
          hint="database completo club"
        />
        <KpiCard
          icon={<Mail className="h-4 w-4" />}
          label="Contattabili email"
          value={
            stats.isLoading
              ? "—"
              : formatNumber(stats.data?.email_contactable ?? 0)
          }
          hint={
            stats.data
              ? `${formatNumber(stats.data.unsubscribed_email)} in blacklist`
              : "meno disiscritti"
          }
        />
        <KpiCard
          icon={<MessageCircle className="h-4 w-4" />}
          label="Contattabili WA"
          value={
            stats.isLoading
              ? "—"
              : formatNumber(stats.data?.whatsapp_contactable ?? 0)
          }
          hint={
            stats.data
              ? `${formatNumber(stats.data.unsubscribed_whatsapp)} in blacklist`
              : "con livello padel"
          }
        />
      </div>

      {/* Active tournaments */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Tornei attivi</CardTitle>
              <CardDescription>
                Tornei non completati in pipeline
              </CardDescription>
            </div>
            <Link
              to="/tournaments"
              className="text-sm font-medium text-court-500 hover:text-court-700"
            >
              Tutti i tornei →
            </Link>
          </div>
        </CardHeader>
        <CardBody className="p-0">
          {tournaments.isLoading ? (
            <div className="p-5 text-sm text-slate-500">Caricamento…</div>
          ) : activeTournaments.length === 0 ? (
            <EmptyState />
          ) : (
            <ul className="divide-y divide-slate-100">
              {activeTournaments.map((t) => {
                const days = daysBetween(new Date(), t.date);
                return (
                  <li key={t.slug}>
                    <Link
                      to={`/tournaments/${t.slug}`}
                      className="flex items-center gap-4 px-5 py-3 transition-colors hover:bg-slate-50"
                    >
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="truncate text-sm font-medium text-slate-900">
                            {t.name}
                          </span>
                          <StatusBadge status={t.status} />
                        </div>
                        <div className="mt-0.5 flex flex-wrap items-center gap-2 text-xs text-slate-500">
                          <Calendar className="h-3 w-3" />
                          <span>{formatItalianDate(t.date, true)}</span>
                          <span>·</span>
                          <span>
                            {days > 0
                              ? `fra ${days} giorni`
                              : days === 0
                                ? "oggi"
                                : `${-days} giorni fa`}
                          </span>
                          {t.next_action ? (
                            <>
                              <span>·</span>
                              <span className="text-court-600">
                                {t.next_action}
                              </span>
                            </>
                          ) : null}
                        </div>
                      </div>
                      <ArrowRight className="h-4 w-4 shrink-0 text-slate-400" />
                    </Link>
                  </li>
                );
              })}
            </ul>
          )}
        </CardBody>
      </Card>

      {/* Quick actions */}
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
        <QuickAction
          to="/tournaments/new"
          icon={<Plus className="h-5 w-5" />}
          title="Nuovo torneo"
          description="Compila il form, genera tutto"
          variant="primary"
        />
        <QuickAction
          to="/tournaments/import-playtomic"
          icon={<CloudDownload className="h-5 w-5" />}
          title="Importa da Playtomic"
          description="Sync automatico da Playtomic"
        />
        <QuickAction
          to="/clients"
          icon={<Users className="h-5 w-5" />}
          title="Clienti"
          description="6324 contatti Playtomic"
        />
        <QuickAction
          to="/calendar"
          icon={<Send className="h-5 w-5" />}
          title="Calendario social"
          description="Cosa esce oggi sui canali"
        />
      </div>

      {/* Footer info */}
      <div className="mt-4 rounded-lg border border-slate-200 bg-white p-4 text-xs text-slate-500">
        <div className="flex flex-wrap items-center gap-4">
          <span>
            API v{health.data?.version ?? "—"} ·{" "}
            {health.data?.status === "ok" ? "✅ ok" : health.data?.status ?? "…"}
          </span>
          <span>
            Cron social: 11:00 &amp; 18:00
          </span>
          <span>CLI resta master (vedi docs/)</span>
        </div>
      </div>
    </div>
  );
}

function KpiCard({
  icon,
  label,
  value,
  hint,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
  hint?: string;
}) {
  return (
    <Card>
      <CardBody>
        <div className="flex items-center gap-2 text-slate-500">
          {icon}
          <span className="text-xs font-medium uppercase tracking-wide">
            {label}
          </span>
        </div>
        <div className="mt-2 text-2xl font-semibold text-slate-900">
          {value}
        </div>
        {hint ? <div className="mt-0.5 text-xs text-slate-400">{hint}</div> : null}
      </CardBody>
    </Card>
  );
}

function QuickAction({
  to,
  icon,
  title,
  description,
  variant = "neutral",
}: {
  to: string;
  icon: React.ReactNode;
  title: string;
  description: string;
  variant?: "neutral" | "primary";
}) {
  return (
    <Link
      to={to}
      className={
        variant === "primary"
          ? "flex items-start gap-3 rounded-lg border border-moving-500 bg-moving-500 p-4 text-white shadow-sm transition-transform hover:scale-[1.01]"
          : "flex items-start gap-3 rounded-lg border border-slate-200 bg-white p-4 shadow-sm transition-colors hover:bg-slate-50"
      }
    >
      <div
        className={
          variant === "primary"
            ? "rounded-md bg-white/20 p-2"
            : "rounded-md bg-slate-100 p-2 text-slate-700"
        }
      >
        {icon}
      </div>
      <div className="flex-1">
        <div
          className={
            variant === "primary"
              ? "text-sm font-semibold"
              : "text-sm font-semibold text-slate-900"
          }
        >
          {title}
        </div>
        <div
          className={
            variant === "primary"
              ? "text-xs opacity-90"
              : "text-xs text-slate-500"
          }
        >
          {description}
        </div>
      </div>
    </Link>
  );
}

function EmptyState() {
  return (
    <div className="flex flex-col items-center justify-center gap-3 px-5 py-10 text-center">
      <Trophy className="h-8 w-8 text-slate-300" />
      <div>
        <div className="text-sm font-medium text-slate-700">
          Nessun torneo attivo
        </div>
        <div className="text-xs text-slate-500">
          Crea il primo torneo per iniziare
        </div>
      </div>
      <Link to="/tournaments/new">
        <Button size="sm">
          <Plus className="h-4 w-4" />
          Nuovo torneo
        </Button>
      </Link>
    </div>
  );
}
